import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  MessageCircle, 
  Send, 
  Users,
  Megaphone,
  Search,
  Wifi,
  WifiOff
} from 'lucide-react';

export default function MessagesPage() {
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // WebSocket URL - convert HTTP to WS
  const getWsUrl = useCallback(() => {
    const apiUrl = API.replace('/api', '');
    const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
    const wsUrl = apiUrl.replace(/^https?/, wsProtocol);
    return `${wsUrl}/ws/${token}`;
  }, [token]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    if (!token || wsRef.current?.readyState === WebSocket.OPEN) return;

    const wsUrl = getWsUrl();
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('WebSocket connected');
      setIsConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        switch (data.type) {
          case 'new_message':
            // Add new message to the list if it's from/to the selected user
            if (selectedUser && 
                (data.data.sender_id === selectedUser.id || 
                 data.data.receiver_id === selectedUser.id)) {
              setMessages(prev => {
                // Avoid duplicates
                if (prev.some(m => m.id === data.data.id)) return prev;
                return [...prev, data.data].sort((a, b) => 
                  new Date(a.created_at) - new Date(b.created_at)
                );
              });
            }
            // Show notification for messages from other users
            if (data.data.sender_id !== user?.id && data.data.sender_id !== selectedUser?.id) {
              toast.info(`New message from ${data.data.sender_name || 'Someone'}`);
            }
            break;
            
          case 'broadcast':
            setBroadcasts(prev => [data.data, ...prev]);
            toast.info('New broadcast received');
            break;
            
          case 'user_online':
            setOnlineUsers(prev => 
              prev.includes(data.data.user_id) ? prev : [...prev, data.data.user_id]
            );
            break;
            
          case 'user_offline':
            setOnlineUsers(prev => prev.filter(id => id !== data.data.user_id));
            break;
            
          case 'typing':
            if (data.data.user_id === selectedUser?.id) {
              setIsTyping(true);
              if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
              typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 2000);
            }
            break;
            
          case 'pong':
            // Keep-alive response
            break;
        }
      } catch (e) {
        console.error('WebSocket message parse error:', e);
      }
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setIsConnected(false);
      // Attempt to reconnect after 3 seconds
      setTimeout(() => {
        if (token) connectWebSocket();
      }, 3000);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    wsRef.current = ws;
  }, [token, getWsUrl, selectedUser, user?.id]);

  // Disconnect WebSocket on unmount
  useEffect(() => {
    connectWebSocket();
    
    // Ping to keep connection alive
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connectWebSocket]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    fetchBroadcasts();
    fetchTrainers();
    fetchOnlineUsers();
    setLoading(false);
  }, [token]);

  useEffect(() => {
    if (selectedUser) {
      fetchMessages(selectedUser.id);
      // Mark messages as read
      markMessagesAsRead(selectedUser.id);
    }
  }, [selectedUser, token]);

  const fetchMessages = async (userId) => {
    try {
      const response = await axios.get(`${API}/messages?receiver_id=${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Sort messages by date (oldest first)
      const sortedMessages = response.data.sort((a, b) => 
        new Date(a.created_at) - new Date(b.created_at)
      );
      setMessages(sortedMessages);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchBroadcasts = async () => {
    try {
      const response = await axios.get(`${API}/messages/broadcasts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBroadcasts(response.data);
    } catch (error) {
      console.error('Error fetching broadcasts:', error);
    }
  };

  const fetchTrainers = async () => {
    try {
      const response = await axios.get(`${API}/trainers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrainers(response.data);
    } catch (error) {
      console.error('Error fetching trainers:', error);
    }
  };

  const fetchOnlineUsers = async () => {
    try {
      const response = await axios.get(`${API}/messages/online-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOnlineUsers(response.data.online_users || []);
    } catch (error) {
      console.error('Error fetching online users:', error);
    }
  };

  const markMessagesAsRead = async (senderId) => {
    try {
      await axios.post(
        `${API}/messages/mark-read?sender_id=${senderId}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedUser) return;
    
    try {
      await axios.post(
        `${API}/messages?receiver_id=${selectedUser.id}&content=${encodeURIComponent(newMessage)}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage('');
      // Message will be added via WebSocket, but fetch as backup
      if (!isConnected) {
        fetchMessages(selectedUser.id);
      }
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN && selectedUser) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        receiver_id: selectedUser.id
      }));
    }
  };

  const isUserOnline = (userId) => onlineUsers.includes(userId);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="messages-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
            <span className="text-primary">MESSAGES</span>
          </h1>
          <p className="text-gray-400 mt-1">Connect with trainers and the community</p>
        </div>
        <div className="flex items-center gap-2">
          {isConnected ? (
            <div className="flex items-center gap-2 text-green-500 text-sm">
              <Wifi className="w-4 h-4" />
              <span>Live</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <WifiOff className="w-4 h-4" />
              <span>Connecting...</span>
            </div>
          )}
        </div>
      </div>

      <Tabs defaultValue="direct" className="w-full">
        <TabsList className="bg-secondary/50 border border-border">
          <TabsTrigger value="direct" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <MessageCircle className="w-4 h-4 mr-2" />
            Direct Messages
          </TabsTrigger>
          <TabsTrigger value="broadcasts" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <Megaphone className="w-4 h-4 mr-2" />
            Broadcasts
          </TabsTrigger>
        </TabsList>

        <TabsContent value="direct" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[600px]">
            {/* Users List */}
            <div className="card-tactical flex flex-col">
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input 
                    placeholder="Search trainers..."
                    className="pl-10 bg-secondary border-transparent"
                  />
                </div>
              </div>
              
              <ScrollArea className="flex-1">
                <div className="space-y-2">
                  {trainers.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No trainers available</p>
                    </div>
                  ) : (
                    trainers.map((trainer) => (
                      <button
                        key={trainer.id}
                        data-testid={`select-user-${trainer.id}`}
                        onClick={() => setSelectedUser(trainer)}
                        className={`w-full flex items-center gap-3 p-3 rounded-sm transition-colors ${
                          selectedUser?.id === trainer.id 
                            ? 'bg-primary/20 border border-primary' 
                            : 'hover:bg-secondary/50'
                        }`}
                      >
                        <div className="relative">
                          <Avatar>
                            <AvatarFallback className="bg-primary/20 text-primary">
                              {trainer.first_name?.[0]}{trainer.last_name?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          {isUserOnline(trainer.id) && (
                            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-card" />
                          )}
                        </div>
                        <div className="text-left flex-1">
                          <div className="text-white font-medium">
                            {trainer.first_name} {trainer.last_name}
                          </div>
                          <div className="text-xs text-gray-400 capitalize flex items-center gap-2">
                            {trainer.role}
                            {isUserOnline(trainer.id) && (
                              <span className="text-green-500">• Online</span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>

            {/* Chat Area */}
            <div className="lg:col-span-2 card-tactical flex flex-col">
              {selectedUser ? (
                <>
                  {/* Chat Header */}
                  <div className="flex items-center gap-3 pb-4 border-b border-border">
                    <div className="relative">
                      <Avatar>
                        <AvatarFallback className="bg-primary/20 text-primary">
                          {selectedUser.first_name?.[0]}{selectedUser.last_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      {isUserOnline(selectedUser.id) && (
                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-card" />
                      )}
                    </div>
                    <div>
                      <div className="text-white font-medium">
                        {selectedUser.first_name} {selectedUser.last_name}
                      </div>
                      <div className="text-xs text-gray-400">
                        {isTyping ? (
                          <span className="text-primary animate-pulse">Typing...</span>
                        ) : isUserOnline(selectedUser.id) ? (
                          <span className="text-green-500">Online</span>
                        ) : (
                          <span>Offline</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 py-4">
                    <div className="space-y-4">
                      {messages.length === 0 ? (
                        <div className="text-center py-8 text-gray-400">
                          <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No messages yet. Start the conversation!</p>
                        </div>
                      ) : (
                        messages.map((msg) => {
                          const isMine = msg.sender_id === user?.id;
                          return (
                            <div
                              key={msg.id}
                              className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`max-w-[70%] p-3 rounded-sm ${
                                  isMine 
                                    ? 'bg-primary text-white' 
                                    : 'bg-secondary text-white'
                                }`}
                              >
                                <p>{msg.content}</p>
                                <div className={`text-xs mt-1 ${isMine ? 'text-white/70' : 'text-gray-400'}`}>
                                  {new Date(msg.created_at).toLocaleTimeString()}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  {/* Input */}
                  <div className="flex gap-2 pt-4 border-t border-border">
                    <Input
                      data-testid="message-input"
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        handleTyping();
                      }}
                      placeholder="Type a message..."
                      className="flex-1 bg-secondary border-transparent"
                      onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    />
                    <Button 
                      data-testid="send-message-btn"
                      onClick={sendMessage}
                      className="bg-primary hover:bg-red-700"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Select a trainer to start messaging</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="broadcasts" className="mt-6">
          <div className="space-y-4">
            {broadcasts.length === 0 ? (
              <div className="text-center py-12 card-tactical">
                <Megaphone className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl text-white mb-2">No broadcasts yet</h3>
                <p className="text-gray-400">Check back later for announcements from trainers</p>
              </div>
            ) : (
              broadcasts.map((broadcast) => (
                <div key={broadcast.id} className="card-tactical">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Megaphone className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      {broadcast.sender_name && (
                        <div className="text-sm text-primary font-medium mb-1">
                          {broadcast.sender_name}
                        </div>
                      )}
                      <p className="text-white">{broadcast.content}</p>
                      <div className="text-xs text-gray-400 mt-2">
                        {new Date(broadcast.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
